import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DefaultFooter from "../components/default-footer";
import Header1 from "../components/default-header";
import { VisaPack } from "../types/visa";
import { API_URL } from "../config";
import axios from "axios";
import { useSeo } from "../components/useSeo";

interface LocationState {
  visaPack?: VisaPack;
  isGuest?: boolean;
  nationality?: string;
}

// Interface pour les documents requis depuis l'API
interface RequiredDocument {
  id: number;
  name: string;
  name_en: string;
  name_ar: string;
  priority: number;
  required: boolean;
}

const STEPS = [
  { label: "Application", iconClass: "icon-user" },
  { label: "Payment", iconClass: "icon-credit-card" },
  { label: "Confirmation", iconClass: "icon-check" },
];

const TABS = [
  "Personal Information",
  "Contact & Travel",
  "Documents",
];

// ── Validation constants ──────────────────────────────────────────────────
const NAME_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,50}$/;
const CITY_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,60}$/;
const PASSPORT_REGEX = /^[A-Za-z0-9]{5,15}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[0-9\s-]{7,16}$/;
const ZIPCODE_REGEX = /^[A-Za-z0-9\s-]{2,10}$/;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const SUBMISSION_STORAGE_KEY = "visa_last_submission";

type FormFieldName =
  | "firstname"
  | "lastname"
  | "nationality"
  | "gender"
  | "passport_no"
  | "current_address"
  | "city"
  | "state"
  | "zipcode"
  | "purpose_of_visit"
  | "email"
  | "contact_no";

const VisaApplication: React.FC = () => {

    useSeo({
    title: "Visa Application | SilverJarz",
    description: "Complete your visa application with SilverJarz.",
    noindex: true,
  });
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state as LocationState | null;
  const visaPackFromState = state?.visaPack || null;

  const [visaPack, setVisaPack] = useState<VisaPack | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [tabIndex, setTabIndex] = useState<number>(0);

  // ── Documents requis (100% dynamiques) ────────────────────────────────
  const [requiredDocuments, setRequiredDocuments] = useState<RequiredDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState<{ [key: number]: File }>({});
  const [fileErrors, setFileErrors] = useState<{ [key: number]: string }>({});
  const [filePreviews, setFilePreviews] = useState<{ [key: number]: string }>({});
  const [previewDocId, setPreviewDocId] = useState<number | null>(null);

  // ── Authentification ────────────────────────────────────────────────────
  const isAuthenticated = !!localStorage.getItem("token_customer_silverjarz");
  const [isGuest, setIsGuest] = useState<boolean>(state?.isGuest ?? false);
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);

  useEffect(() => {
    if (!isAuthenticated && !isGuest) {
      setShowAuthModal(true);
    }
  }, []);

  // ── Profil client (si connecté) ─────────────────────────────────────────
  const [isLoadingProfile, setIsLoadingProfile] = useState<boolean>(false);
  const [lockedFields, setLockedFields] = useState<{
    firstname: boolean;
    lastname: boolean;
    email: boolean;
    nationality: boolean;
  }>({
    firstname: false,
    lastname: false,
    email: false,
    nationality: !!state?.nationality,
  });

  useEffect(() => {
    if (!isAuthenticated) return;

    const token = localStorage.getItem("token_customer_silverjarz");
    if (!token) return;

    const fetchProfile = async () => {
      setIsLoadingProfile(true);
      try {
        const res = await axios.get(`${API_URL}/customer/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const user = res.data?.data;
        const client = user?.client;

        const [fallbackFirst, ...fallbackRestParts] = (user?.name || "").trim().split(" ");
        const fallbackLast = fallbackRestParts.join(" ");

        setFormData((prev) => ({
          ...prev,
          firstname: client?.firstname || fallbackFirst || prev.firstname,
          lastname: client?.lastname || fallbackLast || prev.lastname,
          email: client?.email || user?.email || prev.email,
          contact_no: client?.phone || prev.contact_no,
        }));

        setLockedFields((prev) => ({
          ...prev,
          firstname: !!(client?.firstname || fallbackFirst),
          lastname: !!(client?.lastname || fallbackLast),
          email: !!(client?.email || user?.email),
        }));
      } catch (err) {
        console.error("Failed to load customer profile:", err);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [isAuthenticated]);

  // ── Récupérer les documents requis depuis l'API ────────────────────────
  useEffect(() => {
    const fetchRequiredDocuments = async () => {
      const packId = visaPackFromState?.id;

      if (!packId) {
        console.warn('No visa pack ID found');
        return;
      }

      try {
        setLoadingDocuments(true);
        const response = await axios.get(`${API_URL}/visa-packs/${packId}/documents`);

        if (response.data?.status && response.data?.data?.documents) {
          setRequiredDocuments(response.data.data.documents);
        }
      } catch (error) {
        console.error('Error fetching required documents:', error);
      } finally {
        setLoadingDocuments(false);
      }
    };

    fetchRequiredDocuments();
  }, [visaPackFromState?.id]);

  // États d'exécution API
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submittedReferenceNumber, setSubmittedReferenceNumber] = useState<string>("");

  // Form State - Pas de documents statiques par défaut
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    nationality: state?.nationality || "",
    gender: "",
    passport_no: "",
    current_address: "",
    city: "",
    state: "",
    zipcode: "",
    purpose_of_visit: "",
    email: "",
    contact_no: "",
  });

  // ── Validation state ─────────────────────────────────────────────────────
  const [fieldErrors, setFieldErrors] = useState<{ [key in FormFieldName]?: string }>({});
  const [touchedFields, setTouchedFields] = useState<{ [key in FormFieldName]?: boolean }>({});

  useEffect(() => {
    // Si une soumission vient d'aboutir (même après un refresh), on réaffiche directement
    // la page de confirmation plutôt que de perdre l'utilisateur ou de rouvrir le formulaire.
    const storedSubmissionRaw = sessionStorage.getItem(SUBMISSION_STORAGE_KEY);
    if (storedSubmissionRaw) {
      try {
        const storedSubmission = JSON.parse(storedSubmissionRaw) as {
          visaPack: VisaPack;
          referenceNumber: string;
        };
        if (storedSubmission?.visaPack && storedSubmission?.referenceNumber) {
          setVisaPack(storedSubmission.visaPack);
          setSubmittedReferenceNumber(storedSubmission.referenceNumber);
          setCurrentStep(2);
          return;
        }
      } catch (err) {
        console.error("Failed to restore last submission:", err);
        sessionStorage.removeItem(SUBMISSION_STORAGE_KEY);
      }
    }

    if (visaPackFromState) {
      setVisaPack(visaPackFromState);
    } else {
      navigate("/search-visa");
    }
  }, [visaPackFromState, navigate]);

  // Retourne un message d'erreur (chaîne vide si le champ est valide)
  const validateField = (name: FormFieldName, rawValue: string): string => {
    const value = (rawValue || "").trim();

    switch (name) {
      case "firstname":
      case "lastname":
        if (!value) return "This field is required.";
        if (!NAME_REGEX.test(value)) return "Use letters only, 2–50 characters.";
        return "";

      case "nationality":
        if (!value) return "This field is required.";
        if (value.length < 2) return "Enter a valid nationality.";
        return "";

      case "gender":
        if (!value) return "Select a gender.";
        return "";

      case "passport_no":
        if (!value) return "This field is required.";
        if (!PASSPORT_REGEX.test(value)) return "Enter a valid passport number (5–15 letters or digits).";
        return "";

      case "current_address":
        if (!value) return "This field is required.";
        if (value.length < 5) return "Enter your complete street address.";
        return "";

      case "city":
        if (!value) return "This field is required.";
        if (!CITY_REGEX.test(value)) return "Enter a valid city name.";
        return "";

      case "state":
        if (value && !CITY_REGEX.test(value)) return "Enter a valid state or region.";
        return "";

      case "zipcode":
        if (value && !ZIPCODE_REGEX.test(value)) return "Enter a valid postal code.";
        return "";

      case "purpose_of_visit":
        if (!value) return "Select a purpose of visit.";
        return "";

      case "email":
        if (!value) return "This field is required.";
        if (!EMAIL_REGEX.test(value)) return "Enter a valid email address.";
        return "";

      case "contact_no":
        if (!value) return "This field is required.";
        if (!PHONE_REGEX.test(value)) return "Enter a valid phone number (7–16 digits, may start with +).";
        return "";

      default:
        return "";
    }
  };

  // Champs appartenant à chaque onglet, pour savoir vers lequel naviguer en cas d'erreur
  const TAB_FIELDS: FormFieldName[][] = [
    ["firstname", "lastname", "nationality", "gender", "passport_no"],
    ["current_address", "city", "state", "zipcode", "purpose_of_visit", "email", "contact_no"],
    [],
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target as { name: FormFieldName; value: string };
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Revalider en direct uniquement si le champ a déjà été touché ou est déjà en erreur
    if (touchedFields[name] || fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleInputBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target as { name: FormFieldName; value: string };
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
    setFieldErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  // Un fichier est-il une image que l'on peut afficher directement en miniature ?
  const isImageFile = (file?: File) => !!file && /\.(jpe?g|png)$/i.test(file.name);

  // Gestionnaire pour les fichiers des documents dynamiques uniquement
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, docId: number) => {
    const { files } = e.target;
    const file = files && files[0];
    if (!file) return;

    const isAllowedType = /\.(pdf|jpe?g|png)$/i.test(file.name);
    if (!isAllowedType) {
      setFileErrors((prev) => ({ ...prev, [docId]: "Only PDF, JPG, or PNG files are accepted." }));
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFileErrors((prev) => ({ ...prev, [docId]: "File exceeds the 5MB limit." }));
      return;
    }

    setFileErrors((prev) => {
      const next = { ...prev };
      delete next[docId];
      return next;
    });
    setUploadedFiles((prev) => ({
      ...prev,
      [docId]: file,
    }));

    // Génère une URL d'aperçu locale et révoque l'ancienne pour ce document (évite les fuites mémoire)
    setFilePreviews((prev) => {
      if (prev[docId]) {
        URL.revokeObjectURL(prev[docId]);
      }
      return { ...prev, [docId]: URL.createObjectURL(file) };
    });
  };

  // Révoque toutes les URLs d'aperçu quand le composant est démonté
  useEffect(() => {
    return () => {
      Object.values(filePreviews).forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validation - Vérifie tous les champs personnels/contact, puis les documents dynamiques
  const validateForm = () => {
    const allFieldNames = Object.keys(formData) as FormFieldName[];
    const newErrors: { [key in FormFieldName]?: string } = {};

    allFieldNames.forEach((name) => {
      newErrors[name] = validateField(name, formData[name] as string);
    });

    setFieldErrors(newErrors);
    setTouchedFields(
      allFieldNames.reduce((acc, name) => ({ ...acc, [name]: true }), {} as { [key in FormFieldName]?: boolean })
    );

    // Onglet 01 - Personal Information
    const tab0HasError = TAB_FIELDS[0].some((name) => newErrors[name]);
    if (tab0HasError) {
      setTabIndex(0);
      setErrorMessage("Correct the highlighted fields in Personal Information before continuing.");
      return false;
    }

    // Onglet 02 - Contact & Travel
    const tab1HasError = TAB_FIELDS[1].some((name) => newErrors[name]);
    if (tab1HasError) {
      setTabIndex(1);
      setErrorMessage("Correct the highlighted fields in Contact & Travel before continuing.");
      return false;
    }

    // Onglet 03 - Documents dynamiques
    if (requiredDocuments.length === 0) {
      setTabIndex(2);
      setErrorMessage("No documents found for this visa pack. Please contact support.");
      return false;
    }

    if (Object.keys(fileErrors).length > 0) {
      setTabIndex(2);
      setErrorMessage("Fix the file errors listed under Documents before continuing.");
      return false;
    }

    const missingDocs = requiredDocuments.filter((doc) => !uploadedFiles[doc.id]);
    if (missingDocs.length > 0) {
      setTabIndex(2);
      setErrorMessage(`Please upload all required documents: ${missingDocs.map((d) => d.name).join(', ')}`);
      return false;
    }

    setErrorMessage(null);
    return true;
  };

  // Valide uniquement les champs du tab courant, empêche "Next" si un champ requis est vide/invalide
  const validateTab = (tab: number): boolean => {
    const fields = TAB_FIELDS[tab];
    if (fields.length === 0) return true;

    const updatedErrors: { [key in FormFieldName]?: string } = {};
    fields.forEach((name) => {
      updatedErrors[name] = validateField(name, formData[name] as string);
    });

    setFieldErrors((prev) => ({ ...prev, ...updatedErrors }));
    setTouchedFields((prev) => ({
      ...prev,
      ...fields.reduce((acc, name) => ({ ...acc, [name]: true }), {} as { [key in FormFieldName]?: boolean }),
    }));

    const hasError = fields.some((name) => updatedErrors[name]);
    if (hasError) {
      setErrorMessage(`Complete all required fields in ${TABS[tab]} before continuing.`);
      return false;
    }

    setErrorMessage(null);
    return true;
  };

  const handleNextTab = () => {
    if (validateTab(tabIndex)) {
      setTabIndex(tabIndex + 1);
    }
  };

  const handleProceedToPayment = () => {
    if (validateForm()) {
      setCurrentStep(1);
    }
  };


  // Soumission - Envoie uniquement les documents dynamiques
  const handleSubmit = async () => {
    if (!visaPack) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const payload = new FormData();
      payload.append("visa_pack_id", String(visaPack.id));
      payload.append("firstname", formData.firstname);
      payload.append("lastname", formData.lastname);
      payload.append("nationality", formData.nationality);
      payload.append("gender", formData.gender);
      payload.append("passport_no", formData.passport_no);
      payload.append("current_address", formData.current_address);
      payload.append("city", formData.city);
      if (formData.state) payload.append("state", formData.state);
      if (formData.zipcode) payload.append("zipcode", formData.zipcode);
      payload.append("purpose_of_visit", formData.purpose_of_visit);
      payload.append("email", formData.email);
      payload.append("contact_no", formData.contact_no);
      payload.append("is_guest", isGuest ? "1" : "0");

      // Ajouter uniquement les documents dynamiques
      requiredDocuments.forEach((doc) => {
        const file = uploadedFiles[doc.id];
        if (file) {
          payload.append(`documents[${doc.id}]`, file);
        }
      });

      const response = await axios.post(`${API_URL}/visa-applications`, payload, {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
           Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz")}`
        },
      });

      const data = response.data;

      // Persiste la confirmation : un refresh de la page réaffichera cet écran au lieu du formulaire
      sessionStorage.setItem(
        SUBMISSION_STORAGE_KEY,
        JSON.stringify({ visaPack, referenceNumber: data.reference_number })
      );

      setSubmittedReferenceNumber(data.reference_number);
      setCurrentStep(2);
    } catch (err: any) {
      const serverMessage = err.response?.data?.message || err.message;
      setErrorMessage(serverMessage || "Failed to connect to the server.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Auth modal actions ──────────────────────────────────────────────────
  const handleContinueAsGuest = () => {
    setIsGuest(true);
    setShowAuthModal(false);
  };

  const handleGoToLogin = () => {
    navigate("/login", {
      state: { redirectTo: "/visa-application", pack: visaPack },
    });
  };

  // ── Rendu des documents dynamiques (100% depuis l'API) ────────────────
  const renderDocumentUploads = () => {
    if (loadingDocuments) {
      return (
        <div className="text-center py-20" style={{ gridColumn: "1 / -1" }}>
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-10" style={{ color: "var(--va-muted)" }}>Loading required documents...</p>
        </div>
      );
    }

    if (requiredDocuments.length === 0) {
      return (
        <div className="text-center py-20" style={{ gridColumn: "1 / -1" }}>
          <p style={{ color: "var(--va-muted)" }}>No documents required for this visa pack.</p>
        </div>
      );
    }

    return requiredDocuments.map((doc) => {
      const file = uploadedFiles[doc.id];
      const error = fileErrors[doc.id];
      const previewUrl = filePreviews[doc.id];
      const showImageThumb = !!file && !!previewUrl && isImageFile(file);

      return (
        <div key={doc.id} className="va-field">
          <label>
            {doc.name} *
            <span className="va-field-lock-tag">Required</span>
          </label>
          <div className={`va-upload ${error ? "va-upload-invalid" : ""} ${file ? "has-file" : ""}`}>
            {showImageThumb ? (
              <div
                className="va-upload-thumb-wrap"
                onClick={() => setPreviewDocId(doc.id)}
                role="button"
                aria-label="Preview uploaded image"
              >
                <img src={previewUrl} alt={file!.name} className="va-upload-thumb" />
                <div className="va-upload-thumb-overlay">
                  <i className="icon-eye text-14"></i> Preview
                </div>
              </div>
            ) : (
              <i
                className={file ? "icon-file-text text-26 d-block" : "icon-upload text-26 d-block"}
                style={{ color: "var(--va-primary)" }}
              ></i>
            )}
            <div className="va-upload-name">
              {file ? file.name : "Drag a file or click here"}
            </div>
            <div className="va-upload-hint">
              PDF, JPG, or PNG · Max 5MB
            </div>
            <div className="va-upload-actions">
              <input
                type="file"
                name={`document_${doc.id}`}
                onChange={(e) => handleFileChange(e, doc.id)}
                accept=".pdf,.jpg,.jpeg,.png"
                style={{ display: "none" }}
                id={`input_${doc.id}`}
              />
              <label
                htmlFor={`input_${doc.id}`}
                className="va-upload-btn"
              >
                {file ? "Change File" : "Choose File"}
              </label>
              {file && previewUrl && !showImageThumb && (
                <button
                  type="button"
                  className="va-upload-btn va-upload-btn-ghost"
                  onClick={() => setPreviewDocId(doc.id)}
                >
                  Preview
                </button>
              )}
            </div>
          </div>
          {error && <p className="va-field-error">{error}</p>}
        </div>
      );
    });
  };

  const destinationName = visaPack?.destination_country?.name_en || visaPack?.destination_country?.name || "your destination";
  const visaTypeName = visaPack?.visa_type?.name || visaPack?.visa_type?.name_en || "Tourist Visa";
  const price = visaPack?.price ?? 0;
  const currency = visaPack?.currency || "OMR";

  // Petit utilitaire pour afficher les erreurs sous un champ
  const renderFieldError = (name: FormFieldName) =>
    fieldErrors[name] ? <p className="va-field-error">{fieldErrors[name]}</p> : null;

  const inputClass = (name: FormFieldName) =>
    `va-input ${fieldErrors[name] ? "va-input-invalid" : ""}`;

  const selectClass = (name: FormFieldName) =>
    `va-select ${fieldErrors[name] ? "va-input-invalid" : ""}`;

  if (!visaPack) {
    return (
      <div className="bg-light-2 min-vh-100 d-flex flex-column justify-between">
        <Header1 />
        <div className="container py-80 text-center my-auto">
          <div className="spinner-border text-blue-1 mb-20" role="status"></div>
          <h4 className="text-18 fw-500 text-dark-1">
            Loading your visa application...
          </h4>
        </div>
        <DefaultFooter />
      </div>
    );
  }

  return (
    <div className="va-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,500&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap');
        
        .va-root {
          --va-primary: #294b5a;
          --va-primary-dark: #1b343f;
          --va-gold: #d3be76;
          --va-gold-dark: #b8974c;
          --va-paper: #f7f4ee;
          --va-card: #ffffff;
          --va-border: #e4ded0;
          --va-ink: #22343b;
          --va-muted: #748187;
          --va-error: #9b1c1c;
          --va-error-border: #f8b4b4;
          --va-error-bg: #fdf2f2;
          font-family: 'Inter', -apple-system, sans-serif;
          background: var(--va-paper);
          color: var(--va-ink);
          min-height: 100vh;
        }
        .va-root * { box-sizing: border-box; }
        .va-serif { font-family: 'Fraunces', 'Georgia', serif; }
        .va-mono { font-family: 'IBM Plex Mono', monospace; }

        .va-hero {
          background:
            radial-gradient(circle at 85% 20%, rgba(211,190,118,0.10), transparent 45%),
            radial-gradient(circle at 10% 90%, rgba(211,190,118,0.06), transparent 40%),
            var(--va-primary);
          color: #fff;
          padding: 52px 32px 76px;
          position: relative;
          overflow: hidden;
        }
        .va-hero::before {
          content: "";
          position: absolute; inset: 0;
          background-image: radial-gradient(rgba(255,255,255,0.09) 1px, transparent 1px);
          background-size: 14px 14px;
          opacity: .35;
          pointer-events: none;
        }
        .va-hero-inner { max-width: 1140px; margin: 0 auto; position: relative; }
        .va-hero-row { display: flex; justify-content: space-between; align-items: flex-end; gap: 20px; flex-wrap: wrap; }
        .va-eyebrow {
          letter-spacing: .18em;  font-size: 11.5px;
          color: var(--va-gold); font-weight: 600; display: flex; align-items: center; gap: 8px;
        }
        .va-eyebrow .line { width: 22px; height: 1px; background: var(--va-gold); display: inline-block; }
        .va-hero h1 { font-size: 36px; line-height: 1.15; font-weight: 600; margin: 14px 0 8px; color: #fff; }
        .va-hero h1 em { font-style: italic; color: var(--va-gold); }
        .va-hero p { font-size: 14.5px; color: rgba(255,255,255,0.72); margin: 0; }
        .va-guest-badge {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 9px 16px; border-radius: 999px;
          border: 1px solid rgba(211,190,118,0.4); background: rgba(255,255,255,0.06);
          font-size: 12.5px; color: #fff; font-weight: 500; white-space: nowrap;
        }
        .va-account-badge {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 9px 16px; border-radius: 999px;
          border: 1px solid rgba(211,190,118,0.4); background: rgba(255,255,255,0.06);
          font-size: 12.5px; color: #fff; font-weight: 500; white-space: nowrap;
        }

        .va-shell { max-width: 1140px; margin: 0 auto; padding: 0 32px 80px; }
        .va-stepper {
          background: var(--va-card); border: 1px solid var(--va-border); border-radius: 12px;
          margin-top: -40px; padding: 26px 30px; position: relative; z-index: 2;
          box-shadow: 0 20px 40px -24px rgba(41,75,90,0.28);
          display: flex; align-items: flex-start;
        }
        .va-step { display: flex; flex-direction: column; align-items: center; gap: 8px; flex: 0 0 auto; }
        .va-step-circle {
          width: 34px; height: 34px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 700; border: 2px solid var(--va-border);
          color: var(--va-muted); background: #fff; transition: all .2s ease;
        }
        .va-step.current .va-step-circle {
          background: var(--va-gold); border-color: var(--va-gold); color: var(--va-primary-dark);
          box-shadow: 0 0 0 4px rgba(211,190,118,0.20);
        }
        .va-step.done .va-step-circle {
          background: var(--va-primary); border-color: var(--va-primary); color: #fff;
        }
        .va-step-label {
          font-size: 11px; letter-spacing: .06em; font-weight: 600;
          color: var(--va-muted); text-align: center;
        }
        .va-step.current .va-step-label { color: var(--va-primary); }
        .va-step-connector { flex: 1 1 auto; height: 2px; margin: 16px 10px 0; background-image: linear-gradient(to right, var(--va-border) 60%, transparent 0); background-size: 8px 2px; background-repeat: repeat-x; }
        .va-step-connector.done { background-image: none; background-color: var(--va-gold); }

        .va-grid { display: grid; grid-template-columns: 1fr 360px; gap: 28px; margin-top: 28px; align-items: start; }
        @media (max-width: 960px) { .va-grid { grid-template-columns: 1fr; } }

        .va-card {
          background: var(--va-card); border: 1px solid var(--va-border) !important; border-radius: 12px;
          padding: 36px; position: relative;
        }
        .va-card::before {
          content: ""; position: absolute; top: 14px; left: 14px; width: 22px; height: 22px;
          border-top: 2px solid var(--va-gold); border-left: 2px solid var(--va-gold); border-radius: 2px 0 0 0;
        }
        .va-card::after {
          content: ""; position: absolute; bottom: 14px; right: 14px; width: 22px; height: 22px;
          border-bottom: 2px solid var(--va-gold); border-right: 2px solid var(--va-gold); border-radius: 0 0 2px 0;
        }
        .va-section-eyebrow { font-size: 11px; letter-spacing: .14em;  color: var(--va-gold-dark); font-weight: 700; }
        .va-section-title { font-size: 22px; margin: 6px 0 4px; color: var(--va-ink); }
        .va-rule { width: 42px; height: 2px; background: var(--va-gold); margin-bottom: 26px; }

        .va-alert-error {
          background-color: #fdf2f2; border: 1px solid #f8b4b4; color: #9b1c1c;
          padding: 12px 16px; border-radius: 8px; margin-bottom: 20px; font-size: 13.5px;
        }

        .va-tabs { display: flex; gap: 28px; border-bottom: 1px solid var(--va-border); margin-bottom: 30px;  }
        .va-tab {
          background: none; border: none; cursor: pointer; padding: 0 0 12px;
          font-size: 13.5px; font-weight: 600; color: var(--va-muted);
          border-bottom: 2px solid transparent; margin-bottom: -1px; transition: all .15s ease; white-space: nowrap;
        }
        .va-tab.active { color: var(--va-primary); border-bottom-color: var(--va-gold); }
        .va-tab.has-error { color: var(--va-error); }

        .va-field-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 22px 24px; }
        @media (max-width: 600px) { .va-field-grid { grid-template-columns: 1fr; } }
        .va-field { display: flex; flex-direction: column; gap: 7px; }
        .va-field.full { grid-column: 1 / -1; }
        .va-field label {
          font-size: 10.5px; letter-spacing: .09em;  font-weight: 700; color: var(--va-muted);
          display: flex; align-items: center; gap: 6px;
        }
        .va-field-lock-tag {
          font-size: 9.5px; letter-spacing: 0; text-transform: none; font-weight: 600;
          color: var(--va-gold-dark); background: rgba(211,190,118,0.14);
          padding: 2px 7px; border-radius: 999px;
        }
        .va-field-error {
          font-size: 11.5px; color: var(--va-error); margin: 0; display: flex; align-items: center; gap: 5px;
        }
        .va-root .va-input,
        .va-root .va-select {
          border: 1.5px solid var(--va-border) !important;
          background: #fff !important;
          padding: 11px 14px; font-size: 14.5px; color: var(--va-ink); font-family: 'Inter', sans-serif;
          border-radius: 8px !important; outline: none !important; box-shadow: none !important;
          transition: border-color .15s ease, box-shadow .15s ease; width: 100%;
        }
        .va-input::placeholder { color: #a9b2b6; }
        .va-root .va-input:hover,
        .va-root .va-select:hover {
          border-color: #cfd6d9 !important;
        }
        .va-root .va-input:focus,
        .va-root .va-select:focus {
          border: 1.5px solid var(--va-gold) !important;
          box-shadow: 0 0 0 3px rgba(211,190,118,0.18) !important;
        }
        .va-root .va-input:disabled {
          background: #f4f2ec !important;
          color: #8b959a;
          cursor: not-allowed;
        }
        .va-root .va-input-invalid,
        .va-root .va-input-invalid:focus {
          border: 1.5px solid var(--va-error-border) !important;
          background: var(--va-error-bg) !important;
          box-shadow: 0 0 0 3px rgba(155,28,28,0.10) !important;
        }
        .va-field-hint {
          font-size: 11.5px; color: var(--va-muted); margin: 2px 0 0;
        }
        .va-select { appearance: none; padding-right: 32px; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6' fill='%23748187'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 14px center; }

        .va-upload {
          border: 1.5px dashed var(--va-border) !important; border-radius: 10px; padding: 28px 18px; text-align: center;
          background: #fbf9f4; transition: all .15s ease;
        }
        .va-upload:hover { border-color: var(--va-gold); background: #faf6ea; }
        .va-upload-invalid { border-color: var(--va-error-border) !important; background: var(--va-error-bg); }
        .va-upload.has-file { background: #fbf9f4; }
        .va-upload-name { font-size: 13.5px; font-weight: 600; color: var(--va-ink); margin: 10px 0 2px; word-break: break-all; }
        .va-upload-hint { font-size: 11.5px; color: var(--va-muted); }
        .va-upload-actions { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; }
        .va-upload-btn {
          display: inline-block; margin-top: 14px; padding: 8px 18px; border-radius: 999px;
          border: 1px solid var(--va-primary) !important; color: var(--va-primary); font-size: 12.5px; font-weight: 600;
          cursor: pointer; background: #fff; transition: all .15s ease;
        }
        .va-upload-btn:hover { background: var(--va-primary); color: #fff; }
        .va-upload-btn-ghost { border-color: var(--va-border) !important; color: var(--va-muted); }
        .va-upload-btn-ghost:hover { background: var(--va-paper); color: var(--va-ink); border-color: var(--va-border) !important; }

        .va-upload-thumb-wrap {
          position: relative; width: 100%; height: 120px; border-radius: 8px; overflow: hidden;
          cursor: pointer; margin-bottom: 4px;
        }
        .va-upload-thumb { width: 100%; height: 100%; object-fit: cover; display: block; }
        .va-upload-thumb-overlay {
          position: absolute; inset: 0; background: rgba(27,52,63,0.55); color: #fff;
          display: flex; align-items: center; justify-content: center; gap: 6px;
          font-size: 12.5px; font-weight: 600; opacity: 0; transition: opacity .15s ease;
        }
        .va-upload-thumb-wrap:hover .va-upload-thumb-overlay { opacity: 1; }

        .va-preview-modal {
          background: #fff; border-radius: 16px; width: min(900px, 92vw); max-width: 92vw;
          padding: 22px; position: relative; box-shadow: 0 30px 60px -20px rgba(27,52,63,0.45);
          border: 1px solid var(--va-border); display: flex; flex-direction: column;
        }
        .va-preview-modal-title {
          font-size: 12.5px; font-weight: 700; color: var(--va-ink); margin: 0 34px 14px 0; word-break: break-all;
        }
        .va-preview-modal-body {
          height: 78vh; overflow: auto; display: flex; align-items: center; justify-content: center;
          background: var(--va-paper); border-radius: 8px; border: 1px solid var(--va-border);
        }
        .va-preview-modal-body img { width: 100%; height: 100%; object-fit: contain; }
        .va-preview-modal-body iframe { width: 100%; height: 100%; border: none; }
        .va-preview-modal-link {
          display: inline-block; margin-top: 14px; font-size: 12.5px; font-weight: 600;
          color: var(--va-primary); text-decoration: none;
        }
        .va-preview-modal-link:hover { text-decoration: underline; }

        .va-nav-row { display: flex; justify-content: space-between; align-items: center; padding-top: 28px; margin-top: 30px; border-top: 1px solid var(--va-border); }
        .va-btn { border: none; cursor: pointer; font-size: 13.5px; font-weight: 600; border-radius: 8px; padding: 12px 24px; display: inline-flex; align-items: center; gap: 8px; transition: all .15s ease; }
        .va-btn-ghost { background: transparent; border: 1px solid var(--va-border) !important; color: var(--va-ink); }
        .va-btn-ghost:hover { border-color: var(--va-primary); }
        .va-btn-ghost:disabled { opacity: .4; cursor: not-allowed; }
        .va-btn-primary { background: var(--va-primary); color: #fff; }
        .va-btn-primary:hover { background: var(--va-primary-dark); }
        .va-btn-gold { background: var(--va-gold); color: var(--va-primary-dark); }
        .va-btn-gold:hover { background: var(--va-gold-dark); }
        .va-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .va-pay-option {
          display: flex; align-items: center; justify-content: space-between; gap: 16px;
          border: 1px solid var(--va-border) !important; border-radius: 10px; padding: 20px 22px; margin-bottom: 22px;
        }
        .va-pay-option-title { font-weight: 700; font-size: 14.5px; color: var(--va-ink); }
        .va-pay-option-sub { font-size: 12.5px; color: var(--va-muted); margin-top: 2px; }
        .va-trust-row { display: flex; gap: 22px; flex-wrap: wrap; margin-bottom: 8px; }
        .va-trust-item { display: flex; align-items: center; gap: 7px; font-size: 12.5px; color: var(--va-muted); }

        .va-stamp {
          width: 84px; height: 84px; border-radius: 50%; border: 3px dashed var(--va-gold) !important;
          display: flex; align-items: center; justify-content: center; margin: 0 auto 22px;
          transform: rotate(-6deg); color: var(--va-primary);
        }
        .va-ref {
          display: inline-block; font-size: 13px; padding: 8px 16px; border-radius: 8px;
          background: var(--va-paper); border: 1px solid var(--va-border) !important; color: var(--va-primary); margin: 6px 0 22px;
        }

        .va-ticket { border-radius: 14px; overflow: hidden; border: 1px solid var(--va-border) !important; background: var(--va-card); box-shadow: 0 16px 34px -18px rgba(41,75,90,0.24); position: sticky; top: 90px; }
        .va-ticket-head { background: var(--va-primary); color: #fff; padding: 24px 26px 30px; position: relative; }
        .va-ticket-eyebrow { font-size: 10.5px; letter-spacing: .14em; color: var(--va-gold); font-weight: 700; }
        .va-ticket-dest { font-size: 22px; margin: 8px 0 2px; color: #fff; }
        .va-ticket-type { font-size: 12.5px; color: rgba(255,255,255,0.72); }
        .va-ticket-stamp {
          position: absolute; top: 20px; right: 20px; border: 1px dashed var(--va-gold) !important; color: var(--va-gold);
          font-size: 9px; letter-spacing: .1em;  font-weight: 700; padding: 4px 9px;
          border-radius: 4px; transform: rotate(-6deg);
        }
        .va-perf { position: relative; height: 0; border-top: 2px dashed var(--va-border); margin: 0; }
        .va-perf::before, .va-perf::after {
          content: ""; position: absolute; top: -9px; width: 18px; height: 18px; border-radius: 50%; background: var(--va-paper);
        }
        .va-perf::before { left: -9px; }
        .va-perf::after { right: -9px; }
        .va-ticket-body { padding: 24px 26px; }
        .va-ticket-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 13px; font-size: 13px; }
        .va-ticket-row .k { color: var(--va-muted); display: flex; align-items: center; gap: 6px; }
        .va-ticket-row .v { font-weight: 600; color: var(--va-ink); }
        .va-ticket-total { display: flex; justify-content: space-between; align-items: baseline; border-top: 1px dashed var(--va-border); margin-top: 14px; padding-top: 16px; }
        .va-ticket-total .k { font-size: 14px; font-weight: 700; color: var(--va-ink); }
        .va-ticket-total .v { font-size: 27px; font-weight: 700; color: var(--va-gold-dark); }
        .va-trust-footer {
          display: flex; align-items: center; gap: 10px; background: var(--va-paper); border: 1px solid var(--va-border) !important;
          border-radius: 10px; padding: 13px 15px; margin-top: 16px; font-size: 12px; color: var(--va-muted);
        }

        .va-modal-overlay {
          position: fixed; inset: 0; background: rgba(27, 52, 63, 0.55); backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px;
        }
        .va-modal {
          background: var(--va-card); border-radius: 16px; max-width: 420px; width: 100%;
          padding: 36px 32px 32px; box-shadow: 0 30px 60px -20px rgba(27,52,63,0.45);
          text-align: center; position: relative; border: 1px solid var(--va-border);
        }
        .va-modal-close {
          position: absolute; top: 16px; right: 16px; border: none; background: transparent;
          font-size: 16px; cursor: pointer; color: var(--va-muted); width: 30px; height: 30px;
          border-radius: 50%; display: flex; align-items: center; justify-content: center; transition: background .15s ease;
        }
        .va-modal-close:hover { background: var(--va-paper); color: var(--va-ink); }
        .va-modal-icon {
          width: 60px; height: 60px; border-radius: 50%; background: var(--va-paper);
          color: var(--va-primary); display: flex; align-items: center; justify-content: center;
          margin: 0 auto 18px; border: 1.5px dashed var(--va-gold); font-size: 22px;
        }
        .va-modal h2 { font-size: 20px; font-weight: 600; color: var(--va-ink); margin: 0 0 8px; font-family: 'Fraunces', serif; }
        .va-modal p { font-size: 13.5px; color: var(--va-muted); margin: 0 0 26px; line-height: 1.55; }
        .va-modal-actions { display: flex; flex-direction: column; gap: 12px; }
      `}</style>

      <div className="header-margin"></div>
      <Header1 />

      {showAuthModal && (
        <div className="va-modal-overlay" onClick={() => setShowAuthModal(false)}>
          <div className="va-modal" onClick={(e) => e.stopPropagation()}>
            <button className="va-modal-close" onClick={() => setShowAuthModal(false)}>✕</button>

            <div className="va-modal-icon">
              <i className="icon-lock text-20"></i>
            </div>

            <h2>Sign in to continue</h2>
            <p>
              Sign in to track this application from your dashboard, or continue as a
              guest and fill in your details right away.
            </p>

            <div className="va-modal-actions">
              <button className="va-btn va-btn-gold" style={{ width: "100%", justifyContent: "center" }} onClick={handleGoToLogin}>
                Sign In / Register
              </button>
              <button className="va-btn va-btn-ghost" style={{ width: "100%", justifyContent: "center" }} onClick={handleContinueAsGuest}>
                Continue as Guest
              </button>
            </div>
          </div>
        </div>
      )}

      {previewDocId !== null && uploadedFiles[previewDocId] && filePreviews[previewDocId] && (
        <div className="va-modal-overlay" onClick={() => setPreviewDocId(null)}>
          <div className="va-preview-modal" onClick={(e) => e.stopPropagation()}>
            <button className="va-modal-close" onClick={() => setPreviewDocId(null)}>✕</button>
            <div className="va-preview-modal-title">{uploadedFiles[previewDocId].name}</div>
            <div className="va-preview-modal-body">
              {isImageFile(uploadedFiles[previewDocId]) ? (
                <img
                  src={filePreviews[previewDocId]}
                  alt="Document preview"
                />
              ) : (
                <iframe
                  src={filePreviews[previewDocId]}
                  title="Document preview"
                />
              )}
            </div>
            <a
              href={filePreviews[previewDocId]}
              target="_blank"
              rel="noopener noreferrer"
              className="va-preview-modal-link"
            >
              Open in a new tab ↗
            </a>
          </div>
        </div>
      )}

      <div className="va-hero">
        <div className="va-hero-inner">
          <div className="va-hero-row">
            <div>
              <div className="va-eyebrow">
                <span className="line" />
                Travel Documentation
              </div>
              <h1 className="va-serif">
                Visa <em>Application</em>
              </h1>
              <p>
                Complete your request for{" "}
                <strong style={{ color: "#fff" }}>{destinationName}</strong>
              </p>
            </div>
            {isGuest && !isAuthenticated && (
              <span className="va-guest-badge">
                <i className="icon-user text-14" style={{ color: "var(--va-gold)" }}></i> Guest Mode
              </span>
            )}
            {isAuthenticated && (
              <span className="va-account-badge">
                <i className="icon-check text-14" style={{ color: "var(--va-gold)" }}></i>
                {isLoadingProfile ? "Loading your details..." : "Signed-in account"}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="va-shell">
        <div className="va-stepper">
          {STEPS.map((step, idx) => {
            const stepState =
              currentStep === idx
                ? "current"
                : currentStep > idx
                ? "done"
                : "";
            return (
              <React.Fragment key={step.label}>
                <div className={`va-step ${stepState}`}>
                  <div className="va-step-circle">
                    {currentStep > idx ? (
                      <i className="icon-check text-12"></i>
                    ) : (
                      <i className={`${step.iconClass} text-13`}></i>
                    )}
                  </div>
                  <div className="va-step-label">{step.label}</div>
                </div>
                {idx < STEPS.length - 1 && (
                  <div
                    className={`va-step-connector ${
                      currentStep > idx ? "done" : ""
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="va-grid">
          <div>
            {errorMessage && (
              <div className="va-alert-error">
                {errorMessage}
              </div>
            )}

            {currentStep === 0 && (
              <div className="va-card">
                <div className="va-section-eyebrow">Part 01</div>
                <h2 className="va-serif va-section-title">Applicant Folder</h2>
                <div className="va-rule" />

                <div className="va-tabs">
                  {TABS.map((tab, i) => {
                    const tabHasError = TAB_FIELDS[i].some((name) => fieldErrors[name]);
                    return (
                      <button
                        key={tab}
                        type="button"
                        className={`va-tab ${tabIndex === i ? "active" : ""} ${tabHasError ? "has-error" : ""}`}
                        onClick={() => {
                          // Aller en arrière est toujours permis ; aller en avant exige que le tab courant soit valide
                          if (i <= tabIndex) {
                            setTabIndex(i);
                          } else if (validateTab(tabIndex)) {
                            setTabIndex(i);
                          }
                        }}
                      >
                        {String(i + 1).padStart(2, "0")} — {tab}
                        {tabHasError ? " •" : ""}
                      </button>
                    );
                  })}
                </div>

                {tabIndex === 0 && (
                  <div className="va-field-grid">
                    <div className="va-field">
                      <label>
                        First Name *
                      </label>
                      <input
                        className={inputClass("firstname")}
                        name="firstname"
                        placeholder="e.g. John"
                        value={formData.firstname}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        disabled={lockedFields.firstname}
                      />
                      {renderFieldError("firstname")}
                    </div>
                    <div className="va-field">
                      <label>
                        Last Name *
                      </label>
                      <input
                        className={inputClass("lastname")}
                        name="lastname"
                        placeholder="e.g. Doe"
                        value={formData.lastname}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        disabled={lockedFields.lastname}
                      />
                      {renderFieldError("lastname")}
                    </div>
                    <div className="va-field">
                      <label>
                        Nationality *
                      </label>
                      <input
                        className={inputClass("nationality")}
                        name="nationality"
                        placeholder="e.g. American"
                        value={formData.nationality}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        disabled={lockedFields.nationality}
                      />
                      {renderFieldError("nationality")}
                    </div>
                    <div className="va-field">
                      <label>Gender *</label>
                      <select
                        className={selectClass("gender")}
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                      >
                        <option value="">Select</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                      {renderFieldError("gender")}
                    </div>
                    <div className="va-field full">
                      <label>Passport Number *</label>
                      <input
                        className={inputClass("passport_no")}
                        name="passport_no"
                        placeholder="e.g. 20AA12345"
                        value={formData.passport_no}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                      />
                      {renderFieldError("passport_no")}
                    </div>
                  </div>
                )}

                {tabIndex === 1 && (
                  <div className="va-field-grid">
                    <div className="va-field full">
                      <label>Current Address *</label>
                      <input
                        className={inputClass("current_address")}
                        name="current_address"
                        placeholder="Street number and name"
                        value={formData.current_address}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                      />
                      {renderFieldError("current_address")}
                    </div>
                    <div className="va-field">
                      <label>City *</label>
                      <input
                        className={inputClass("city")}
                        name="city"
                        placeholder="City"
                        value={formData.city}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                      />
                      {renderFieldError("city")}
                    </div>
                    <div className="va-field">
                      <label>State / Region</label>
                      <input
                        className={inputClass("state")}
                        name="state"
                        placeholder="State / Region"
                        value={formData.state}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                      />
                      {renderFieldError("state")}
                    </div>
                    <div className="va-field">
                      <label>ZIP / Postal Code</label>
                      <input
                        className={inputClass("zipcode")}
                        name="zipcode"
                        placeholder="Postal Code"
                        value={formData.zipcode}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                      />
                      {renderFieldError("zipcode")}
                    </div>
                    <div className="va-field">
                      <label>
                        Email Address *
                        {lockedFields.email && <span className="va-field-lock-tag">From account</span>}
                      </label>
                      <input
                        className={inputClass("email")}
                        type="email"
                        name="email"
                        placeholder="name@example.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        disabled={lockedFields.email}
                      />
                      {renderFieldError("email")}
                    </div>
                    <div className="va-field">
                      <label>Phone Number *</label>
                      <input
                        className={inputClass("contact_no")}
                        name="contact_no"
                        placeholder="+1 555 123 4567"
                        value={formData.contact_no}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                      />
                      {renderFieldError("contact_no")}
                    </div>
                    <div className="va-field full">
                      <label>Purpose of Visit *</label>
                      <select
                        className={selectClass("purpose_of_visit")}
                        name="purpose_of_visit"
                        value={formData.purpose_of_visit}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                      >
                        <option value="">Select purpose</option>
                        <option value="tourism">Tourism / Vacation</option>
                        <option value="business">Business / Conference</option>
                        <option value="study">Study</option>
                        <option value="work">Work</option>
                      </select>
                      {renderFieldError("purpose_of_visit")}
                    </div>
                  </div>
                )}

                {tabIndex === 2 && (
                  <div className="va-field-grid">
                    {renderDocumentUploads()}
                  </div>
                )}

                <div className="va-nav-row">
                  <button
                    type="button"
                    className="va-btn va-btn-ghost"
                    disabled={tabIndex === 0}
                    onClick={() => setTabIndex(tabIndex - 1)}
                  >
                    ‹ Previous
                  </button>
                  {tabIndex < 2 ? (
                    <button
                      type="button"
                      className="va-btn va-btn-primary"
                      onClick={handleNextTab}
                    >
                      Next ›
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="va-btn va-btn-gold"
                      onClick={handleProceedToPayment}
                    >
                      Proceed to Payment ›
                    </button>
                  )}
                </div>
              </div>
            )}

            {currentStep === 1 && (
              <div className="va-card">
                <div className="va-section-eyebrow">Part 02</div>
                <h2 className="va-serif va-section-title">Secure Payment</h2>
                <div className="va-rule" />

                <div className="va-pay-option">
                  <div>
                    <div className="va-pay-option-title">
                      Card Payment
                    </div>
                    <div className="va-pay-option-sub">
                      100% encrypted transaction via SSL protocol
                    </div>
                  </div>
                  <i
                    className="icon-credit-card text-22"
                    style={{ color: "var(--va-primary)" }}
                  ></i>
                </div>

                <div className="va-trust-row">
                  <span className="va-trust-item">
                    <i className="icon-lock text-13"></i> 256-bit Encryption
                  </span>
                  <span className="va-trust-item">
                    <i className="icon-shield text-13"></i> PCI-DSS Compliant
                  </span>
                  <span className="va-trust-item">
                    <i className="icon-check text-13"></i> Instant Receipt
                  </span>
                </div>

                <div className="va-nav-row">
                  <button
                    type="button"
                    className="va-btn va-btn-ghost"
                    disabled={isSubmitting}
                    onClick={() => setCurrentStep(0)}
                  >
                    ‹ Back to Form
                  </button>
                  <button
                    type="button"
                    className="va-btn va-btn-gold"
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Processing...
                      </>
                    ) : (
                      `Pay ${price} ${currency}`
                    )}
                  </button>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div
                className="va-card"
                style={{ textAlign: "center", padding: "56px 40px" }}
              >
                <div className="va-stamp">
                  <i className="icon-check text-30"></i>
                </div>
                <h2
                  className="va-serif"
                  style={{ fontSize: 24, marginBottom: 8 }}
                >
                  Application Submitted Successfully!
                </h2>
                <p
                  style={{
                    fontSize: 14,
                    color: "var(--va-muted)",
                    maxWidth: 380,
                    margin: "0 auto",
                  }}
                >
                  Your file is now being processed. Estimated processing time is{" "}
                  <strong style={{ color: "var(--va-ink)" }}>
                    {visaPack.processing_time_en}
                  </strong>
                  .
                </p>

                <div className="va-ref va-mono">{submittedReferenceNumber}</div>

                <div>
                  <button
                    type="button"
                    className="va-btn va-btn-primary"
                    onClick={() => {
                      sessionStorage.removeItem(SUBMISSION_STORAGE_KEY);
                      navigate("/");
                    }}
                  >
                    Return to Homepage
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="va-ticket">
            <div className="va-ticket-head">
              <div className="va-ticket-stamp">Verified</div>
              <div className="va-ticket-eyebrow">Visa Summary</div>
              <div className="va-ticket-dest va-serif">{destinationName}</div>
              <div className="va-ticket-type">{visaTypeName}</div>
            </div>
            <div className="va-perf" />
            <div className="va-ticket-body">
              <div className="va-ticket-row">
                <span className="k">
                  <i className="icon-destination text-12"></i> Entry Type
                </span>
                <span className="v">{visaPack.entry_type}</span>
              </div>
              <div className="va-ticket-row">
                <span className="k">
                  <i className="icon-clock text-12"></i> Processing Time
                </span>
                <span className="v" style={{ color: "var(--va-primary)" }}>
                  {visaPack.processing_time_en}
                </span>
              </div>
              <div className="va-ticket-row">
                <span className="k">
                  <i className="icon-calendar text-12"></i> Visa Validity
                </span>
                <span className="v">{visaPack.visa_validity_en}</span>
              </div>
              <div className="va-ticket-row">
                <span className="k">
                  <i className="icon-bed text-12"></i> Stay Validity
                </span>
                <span className="v">{visaPack.stay_validity_en}</span>
              </div>

              <div className="va-ticket-total">
                <span className="k va-serif">Total Fee</span>
                <span className="v va-serif va-mono">
                  {price} {currency}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="va-trust-footer" style={{ maxWidth: 720 }}>
          <i
            className="icon-shield text-16"
            style={{ color: "var(--va-primary)" }}
          ></i>
          Every document is encrypted during transfer and reviewed by our certified visa specialists.
        </div>
      </div>

      <DefaultFooter />
    </div>
  );
};

export default VisaApplication;